"use client";
import React from "react";
import ReactDOM from "react-dom";

import { Box } from "../../foundations";
import { useOverlay } from "../../hooks/useOverlay";

export type OverlayProps = React.ComponentProps<typeof Box> & {
  visible: boolean;
  onDismiss?: () => void;
  children?: React.ReactNode;
};

export const Overlay: React.FC<OverlayProps> = ({
  visible,
  onDismiss,
  sx,
  children
}) => {
  const [, setForceUpdate] = React.useState(0);
  const [hasTransitionedOut, setHasTransitionedOut] = React.useState(true);
  const { mountOverlay, unmountOverlay, getOverlayZIndex } = useOverlay();
  const overlayIdRef = React.useRef<number>(0);
  const overlayZIndex = getOverlayZIndex(overlayIdRef.current);
  const portalRef = React.useRef<Element | null>(null);
  const background = React.useMemo(() => {
    return overlayZIndex === 1 ? "scrim" : "unset";
  }, [overlayZIndex]);

  const handleClick = React.useCallback(
    (e: React.MouseEvent) => {
      // If the overlay is being clicked
      if (e.target === e.currentTarget) {
        onDismiss?.();
      }
    },
    [onDismiss]
  );

  const handleTransitionEnd = React.useCallback(
    (e: React.TransitionEvent) => {
      if (e.currentTarget === e.target && !visible) {
        setHasTransitionedOut(true);
        unmountOverlay(overlayIdRef.current);
      }
    },
    [unmountOverlay, visible]
  );

  React.useEffect(() => {
    // Force a render on mount so the element exists and
    // transitions start working
    setForceUpdate(1);
  }, []);

  React.useEffect(() => {
    portalRef.current = document.createElement("div");

    if (portalRef.current) {
      document.body.appendChild(portalRef.current);
    }
    return () => {
      if (portalRef.current) {
        document.body.removeChild(portalRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    const handleDocumentKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "escape" || e.key.toLowerCase() === "esc") {
        onDismiss?.();
      }
    };
    window.addEventListener("keydown", handleDocumentKeyDown);
    return () => {
      window.removeEventListener("keydown", handleDocumentKeyDown);
    };
  }, [onDismiss]);

  React.useEffect(
    () => () => {
      // Unmount the overlay from the stack if the component
      // is unmounted from the DOM
      unmountOverlay(overlayIdRef.current);
    },
    [unmountOverlay]
  );

  React.useEffect(() => {
    if (visible && hasTransitionedOut) {
      const newOverlayId = mountOverlay();
      overlayIdRef.current = newOverlayId;
      setHasTransitionedOut(false);
    }
  }, [hasTransitionedOut, mountOverlay, visible]);

  if (!portalRef.current) {
    return null;
  }
  return ReactDOM.createPortal(
    <Box
      aria-hidden={!visible}
      role="dialog"
      sx={{
        ...sx,
        background,
        backdropFilter: "blur(12px)",
        height: !hasTransitionedOut ? "100%" : 0,
        left: 0,
        opacity: !visible ? 0 : 1,
        overflow: "hidden",
        position: "fixed",
        top: 0,
        transition: "transition-ease-out-medium",
        width: "100%",
        zIndex: (theme) => theme?.zIndices?.modal + overlayZIndex
      }}
      onClick={handleClick}
      onTransitionEnd={handleTransitionEnd}
    >
      {visible || !hasTransitionedOut ? children : null}
    </Box>,
    portalRef.current
  );
};
